import { useEffect, useMemo, useRef } from 'react'

import { ConnectionState, IAgoraRTCRemoteUser } from 'agora-rtc-react'
import { usePrevious } from 'src/hooks/use-previous'

import { playBellSound } from '../utils/bellSound'
import { isScreenShareUserUid } from '../utils/screenShare'

type UseJoinNotificationSoundProps = {
  connectionState: ConnectionState
  remoteUsers: IAgoraRTCRemoteUser[]
  enabled?: boolean
}

/** ビデオ通話入室時（自分・他ユーザー）にベル音を鳴らす */
export const useJoinNotificationSound = ({
  connectionState,
  remoteUsers,
  enabled = true,
}: UseJoinNotificationSoundProps): void => {
  // 再接続時に二重再生しないよう、初回 CONNECTED 時のみ鳴らす
  const hasPlayedOnInitialConnectRef = useRef<boolean>(false)

  // 自分の入室検知
  useEffect(() => {
    if (
      enabled &&
      !hasPlayedOnInitialConnectRef.current &&
      connectionState === 'CONNECTED'
    ) {
      hasPlayedOnInitialConnectRef.current = true
      playBellSound()
    }
  }, [connectionState, enabled])

  const remoteUserCount = useMemo(() => {
    return remoteUsers.filter(user => !isScreenShareUserUid(user.uid)).length
  }, [remoteUsers])

  const previousRemoteUserCount = usePrevious(remoteUserCount)

  // 他ユーザーの入室検知（画面共有ユーザーは除外）
  useEffect(() => {
    if (
      enabled &&
      previousRemoteUserCount !== null && // 初回レンダリング時はスキップ
      remoteUserCount > previousRemoteUserCount
    ) {
      playBellSound()
    }
  }, [enabled, remoteUserCount, previousRemoteUserCount])
}
